// 何もない空白部を右クリックしたときに開くコンテキストメニュー。
// 現状の項目は「貼り付け」1 つのみ（クリップボードに貼り付け可能な内容が無ければグレーアウト）。
//
// 表示位置のクランプ・外側クリック / Escape / スクロール / リサイズでの閉じる副作用は
// object-context-menu.tsx と同じ作法に揃える。貼り付け先は「開いた瞬間の右クリック位置」の
// ステージ座標（emptyContextMenu.stageX/Y）を使い、メニュー項目へマウスを動かしても位置がずれない。

import { ContentPasteIconData } from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { probeClipboardPasteable } from "../input/use-paste-handler";

const MENU_WIDTH_PX = 180;
const MENU_MARGIN_PX = 8;

interface Props {
  /** 「貼り付け」実行。貼り付け先のステージ座標を受け取る。 */
  readonly onPaste: (position: { x: number; y: number }) => void;
}

export function EmptyAreaContextMenu({ onPaste }: Props): JSX.Element | null {
  const emptyContextMenu = useCanvasStore((s) => s.emptyContextMenu);
  const closeEmptyContextMenu = useCanvasStore((s) => s.closeEmptyContextMenu);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  // 貼り付け可否（クリップボード内容の有無）。開くたびに best-effort で判定する。
  // 楽観的に活性を初期値にし、判定が "empty" のときだけ非活性へ落とす（"unknown" では活性のまま）。
  const [pasteEnabled, setPasteEnabled] = useState(true);

  // 開くたびに貼り付け可否を判定する。
  useEffect(() => {
    if (!emptyContextMenu) return;
    let cancelled = false;
    setPasteEnabled(true);
    void probeClipboardPasteable().then((result) => {
      if (!cancelled) setPasteEnabled(result !== "empty");
    });
    return () => {
      cancelled = true;
    };
  }, [emptyContextMenu]);

  // メニュー外クリック / Escape / スクロールで閉じる（object-context-menu と同じ）。
  useEffect(() => {
    if (!emptyContextMenu) return undefined;
    const onDown = (event: MouseEvent | PointerEvent): void => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) return;
      closeEmptyContextMenu();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") closeEmptyContextMenu();
    };
    const onScroll = (): void => closeEmptyContextMenu();
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("wheel", onScroll, { passive: true });
    window.addEventListener("resize", closeEmptyContextMenu);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onScroll);
      window.removeEventListener("resize", closeEmptyContextMenu);
    };
  }, [emptyContextMenu, closeEmptyContextMenu]);

  // 画面端で右や下へはみ出す場合は左 / 上へ寄せる。
  useLayoutEffect(() => {
    if (!emptyContextMenu) {
      setPosition(null);
      return;
    }
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth ?? MENU_WIDTH_PX;
    const menuHeight = menuEl?.offsetHeight ?? 48;
    const maxLeft = window.innerWidth - menuWidth - MENU_MARGIN_PX;
    const maxTop = window.innerHeight - menuHeight - MENU_MARGIN_PX;
    setPosition({
      left: Math.max(MENU_MARGIN_PX, Math.min(emptyContextMenu.clientX, maxLeft)),
      top: Math.max(MENU_MARGIN_PX, Math.min(emptyContextMenu.clientY, maxTop)),
    });
  }, [emptyContextMenu]);

  if (!emptyContextMenu) return null;

  const handlePaste = (): void => {
    if (!pasteEnabled) return;
    onPaste({ x: emptyContextMenu.stageX, y: emptyContextMenu.stageY });
    closeEmptyContextMenu();
  };

  return (
    <ul
      ref={menuRef}
      role="menu"
      aria-label="キャンバスのコンテキストメニュー"
      data-testid="empty-area-context-menu"
      style={{
        position: "fixed",
        left: position?.left ?? emptyContextMenu.clientX,
        top: position?.top ?? emptyContextMenu.clientY,
        margin: 0,
        padding: "4px",
        listStyle: "none",
        minWidth: `${MENU_WIDTH_PX}px`,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-2)",
        boxShadow: "var(--shadow-2)",
        zIndex: 1000,
        fontSize: "13px",
        color: "var(--color-text)",
      }}
    >
      <li role="none" style={{ margin: 0 }}>
        <button
          role="menuitem"
          type="button"
          data-testid="empty-area-context-menu-paste"
          disabled={!pasteEnabled}
          onClick={handlePaste}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "8px 12px",
            background: "transparent",
            border: "none",
            borderRadius: "var(--radius-1)",
            textAlign: "left",
            cursor: pasteEnabled ? "pointer" : "not-allowed",
            opacity: pasteEnabled ? 1 : 0.5,
            color: "inherit",
            font: "inherit",
          }}
          onMouseEnter={(event) => {
            if (pasteEnabled) event.currentTarget.style.background = "var(--color-surface-muted)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = "transparent";
          }}
        >
          <Icon d={ContentPasteIconData} />
          <span>貼り付け</span>
        </button>
      </li>
    </ul>
  );
}
